/**
 * @file: provider.tsx
 *
 * Owns the chat contact list: the initial load and refreshes. Contacts
 * have no live-event stream — they change only on explicit refresh — so
 * this provider is intentionally thin next to `ChatThreadsProvider`.
 *
 * Scope it to the surface that shows contacts (the inbox mini-app), not
 * app-wide, so the list is created/torn down with that surface.
 */

import { ChatContact } from '@euler/model/chat';
import { useServiceFactory } from '@euler/services/factory';
import { useCreation, useWatchValue } from '@euler/utils/hooks';
import { FC, ReactNode, memo, useCallback, useEffect, useMemo } from 'react';
import { BehaviorSubject } from 'rxjs';
import { useChatSession } from '../session/context';
import { ChatContactsContext } from './context';
import { ChatContactsStore } from './types';

export const ChatContactsProvider: FC<{ children: ReactNode }> = memo(
  ({ children }) => {
    const { chatService } = useServiceFactory();
    const { me } = useChatSession();
    const myPlatformUserId = me.platformUserId;

    const chatContacts$ = useCreation(
      () => new BehaviorSubject<ChatContact[]>([]),
      [],
    );

    const refreshChatContacts = useCallback(
      async (fetcher: () => Promise<ChatContact[]>) => {
        try {
          chatContacts$.next(await fetcher());
        } catch (e) {
          console.error('[chat-contacts] refresh contacts failed', e);
        }
      },
      [chatContacts$],
    );

    // Initial load — own concern, not the transport's. Re-runs on account
    // switch (myPlatformUserId change).
    useEffect(() => {
      if (!myPlatformUserId) return;
      void refreshChatContacts(() => chatService.listContacts());
    }, [myPlatformUserId, chatService, refreshChatContacts]);

    // Account switch within the same auth-context key — wipe the list.
    // `!prev` skips both the mount call and the initial login.
    useWatchValue(myPlatformUserId, prev => {
      if (!prev) return;
      chatContacts$.next([]);
    });

    const store = useMemo<ChatContactsStore>(
      () => ({ chatContacts$, refreshChatContacts }),
      [chatContacts$, refreshChatContacts],
    );

    return (
      <ChatContactsContext.Provider value={store}>
        {children}
      </ChatContactsContext.Provider>
    );
  },
);
